/**
 * The preview, as a modal.
 *
 * Checking the preview is a moment, not a mode: the user glances at how the post
 * lands on each network and goes back to writing. Holding a permanent pane open
 * for it costs width on every other screen for something looked at a handful of
 * times, so it opens over the top and closes again.
 *
 * The frame is capped at a feed's real width — stretching it to the dialog would
 * preview a layout no network actually renders.
 */

import { useTranslation } from '../../../context/LanguageContext';
import { Dialog, DialogContent, DialogTitle } from '../../core/Dialog';
import { PlatformPreview } from './PlatformPreview';
import type { PlatformId } from '../../../config/platforms';
import type { ComposerMedia, ConnectedAccount } from '../../../types/composer';

interface PreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  text: string;
  media: ComposerMedia[];
  selectedPlatforms: PlatformId[];
  accounts: ConnectedAccount[];
}

export function PreviewDialog({
  open,
  onOpenChange,
  text,
  media,
  selectedPlatforms,
  accounts,
}: Readonly<PreviewDialogProps>) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl p-0">
        <DialogTitle className="border-b border-slate-200 px-5 py-3.5 text-[15px]">
          {t('composer.section.preview')}
        </DialogTitle>

        {/* The dialog is centred and fixed, so a tall post has to scroll inside
            it rather than growing off the top and bottom of the viewport. */}
        <div className="max-h-[75vh] overflow-y-auto p-5">
          <PlatformPreview
            text={text}
            media={media}
            selectedPlatforms={selectedPlatforms}
            accounts={accounts}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
